<template>
    <b-modal id="app-update-modal" :visible="show" content-class="argus-glass-dialog app-update-dialog"
             hide-header-close no-close-on-backdrop no-close-on-esc @hide="preventClose">
        <template #modal-title>
            <span class="app-update-heading">
                <span class="app-update-icon" :class="iconClass" aria-hidden="true"><i :class="icon"/></span>
                {{ title }}
            </span>
        </template>
        <div role="status" aria-live="polite">
            <p v-if="!hasError || offlineOnly" class="mb-3">
                <b-spinner v-if="!ready || checking || busy" small class="mr-2 app-update-spinner" aria-hidden="true"/>
                {{ message }}
            </p>
            <p v-if="!offlineOnly && ready && serverVersion" class="mb-1 app-update-version">Новая версия: {{ serverVersion }}</p>
            <p v-if="!offlineOnly && clientVersion" class="mb-0 text-muted app-update-version">Текущая версия: {{ clientVersion }}</p>
        </div>
        <template #modal-footer>
            <div class="w-100">
                <template v-if="offlineOnly">
                    <p class="small text-muted mb-2">Проверяем подключение автоматически. Введённые данные остаются на странице.</p>
                    <b-button variant="primary" block :disabled="checking" @click="checkNow">
                        {{ checking ? 'Проверяем связь…' : 'Проверить сейчас' }}
                    </b-button>
                </template>
                <template v-else>
                    <p v-if="hasError" role="alert" class="app-update-feedback mb-2">{{ errorMessage }}</p>
                    <p class="small text-muted mb-2">Для продолжения работы требуется обновление. Несохранённые данные при перезагрузке могут потеряться.</p>
                    <b-button variant="primary" block :disabled="!ready || busy" @click="confirmUpdate">
                        {{ navigating ? 'Открываем приложение…' : confirming ? 'Проверяем готовность…' : 'Обновить страницу' }}
                    </b-button>
                </template>
            </div>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: 'AppUpdateModal',
    computed: {
        update() { return this.$store.state.appVersion; },
        show() { return this.$store.getters['appVersion/connectionBlocking']; },
        offlineOnly() { return this.update.connectionLost && !this.update.isUpdateRequired; },
        clientVersion() { return this.update.clientVersion; },
        serverVersion() { return this.update.serverVersion; },
        checking() { return this.update.checking; },
        confirming() { return this.update.confirming; },
        navigating() { return this.update.navigating; },
        busy() { return this.checking || this.confirming || this.navigating; },
        ready() { return this.update.status === 'ready'; },
        hasError() { return this.update.navigationError || ['failed', 'unavailable'].includes(this.update.status) ||
            (this.update.status === 'unknown' && !this.checking); },
        icon() { return this.ready ? 'fas fa-check-circle' : this.offlineOnly ? 'fas fa-plug' : 'fas fa-sync-alt'; },
        iconClass() { return this.ready ? 'app-update-icon--ready' : this.offlineOnly ? 'app-update-icon--offline' : ''; },
        title() {
            if (this.offlineOnly) return 'Нет связи с сервером';
            if (this.ready) return 'Обновление готово';
            if (this.update.status === 'building') return 'Приложение обновляется';
            if (this.update.status === 'unknown') return this.checking ? 'Проверяем готовность обновления' : 'Нет связи с сервером';
            return 'Обновление временно недоступно';
        },
        message() {
            if (this.offlineOnly) return this.checking
                ? 'Проверяем подключение. Окно закроется само, когда связь восстановится.'
                : 'Сервер временно недоступен. Окно закроется само после восстановления связи.';
            if (this.navigating) return 'Открываем обновлённое приложение. Это может занять время при медленном соединении.';
            if (this.confirming) return 'Проверяем готовность перед переходом. Пожалуйста, подождите.';
            if (this.ready) return 'Обновление завершено. Перезагрузите страницу, чтобы продолжить работу.';
            if (this.update.status === 'building') return 'Подождите, пока обновление завершится. Не перезагружайте страницу.';
            if (this.update.status === 'unknown' && this.checking) return 'Проверяем готовность обновления. Не перезагружайте страницу.';
            if (this.update.status === 'unknown') return 'Не удалось проверить готовность: нет связи с сервером. Проверка повторится автоматически.';
            return 'Обновление не завершилось. Ожидаем восстановления приложения. Не перезагружайте страницу.';
        },
        errorMessage() { return this.update.navigationError ? 'Не удалось начать переход. Повторите попытку.' : this.message; },
    },
    methods: {
        preventClose(event) { if (this.show) event.preventDefault(); },
        confirmUpdate() { this.$store.dispatch('appVersion/confirmUpdate'); },
        checkNow() { this.$store.dispatch('appVersion/checkNow'); },
    },
};
</script>
<style scoped>
.app-update-heading { display: inline-flex; align-items: center; gap: 10px; font-size: 1.1rem; font-weight: 650; }
.app-update-icon { display: inline-flex; align-items: center; justify-content: center; flex: none; width: 34px; height: 34px;
    border-radius: 10px; background: #e8f2fc; color: #27649b; font-size: 15px; }
.app-update-icon--offline { background: #fff1e6; color: #a75a22; }
.app-update-icon--ready { background: #e7f6ee; color: #23805a; }
.app-update-feedback { padding: 9px 11px; border-radius: 8px; background: #fff1ed; color: #8c332b; font-size: .9rem; }
.app-update-version { overflow-wrap: anywhere; }
/* ShiftReport has global !important spinner dimensions. Keep this modal compact. */
.app-update-spinner { width: 1rem !important; height: 1rem !important; border-width: 2px; vertical-align: middle; }
</style>
<style>
.modal-content.app-update-dialog { background: #fbfdff; }
.app-update-dialog > .modal-header,
.app-update-dialog > .modal-footer { background: #f8fafc; }
</style>
